import { useEffect, useRef, useState } from "react";
import type { Role } from "@/context/BranchContext";
import { ROLE_LIST, roleMeta, roleTierIconTone, roleTierSoftBadge } from "@/config/roles";
import Button from "@/components/base/Button";

interface RoleSelectProps {
  role: Role;
  onRoleChange: (role: Role) => void;
  onPreview: () => void;
}

export default function RoleSelect({ role, onRoleChange, onPreview }: RoleSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const activeMeta = roleMeta(role);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleSelect = (next: Role) => {
    onRoleChange(next);
    setOpen(false);
  };

  return (
    <div className="rounded-card border border-background-200 bg-background-100/60 p-4 space-y-4">
      <div>
        <label className="block mb-2 text-sm font-semibold text-foreground-700">
          معاينة لوحة الموظف
        </label>

        <div ref={rootRef} className="relative">
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className={`w-full h-12 pl-3.5 pr-3.5 flex items-center gap-2.5 rounded-btn border bg-background-50 transition-all cursor-pointer ${
              open
                ? "border-primary-500 ring-2 ring-primary-100"
                : "border-background-300 hover:border-background-400"
            }`}
          >
            <span
              className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 ${roleTierSoftBadge(role)}`}
            >
              <i className={`${activeMeta.icon} text-sm`}></i>
            </span>
            <span className="min-w-0 flex-1 text-right text-sm font-semibold text-foreground-900 truncate">
              {activeMeta.label}
            </span>
            <i
              className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            ></i>
          </button>

          {open ? (
            <div
              role="listbox"
              className="absolute z-40 left-0 right-0 top-full mt-2 rounded-card border border-background-200 bg-background-50 shadow-dropdown overflow-hidden animate-fade-slide-down"
            >
              <div className="max-h-[320px] overflow-y-auto py-1">
                {ROLE_LIST.map((meta, index) => {
                  const isActive = meta.key === role;
                  const showDivider = index > 0 && ROLE_LIST[index - 1].tier !== meta.tier;
                  return (
                    <div key={meta.key}>
                      {showDivider ? (
                        <div className="mx-3 my-1 border-t border-background-100"></div>
                      ) : null}
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => handleSelect(meta.key)}
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-right transition-colors cursor-pointer ${
                          isActive ? "bg-primary-50" : "hover:bg-background-100"
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                            isActive
                              ? "border-primary-500 bg-primary-500 text-background-50"
                              : "border-background-300"
                          }`}
                        >
                          {isActive ? <i className="ri-check-line text-xs"></i> : null}
                        </span>
                        <span
                          className={`w-4 h-4 flex items-center justify-center rounded shrink-0 ${roleTierIconTone(
                            meta.key,
                          )}`}
                        >
                          <i className={`${meta.icon} text-sm`}></i>
                        </span>
                        <span className="text-sm font-semibold text-foreground-900 truncate">
                          {meta.label}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        icon="ri-eye-line"
        onClick={onPreview}
      >
        معاينة اللوحة
      </Button>

      <p className="text-[11px] text-foreground-500 leading-relaxed">
        معاينة التصميم فقط — يتم تحديد الدور والصلاحيات من لوحة المحل التجاري.
      </p>
    </div>
  );
}